import type {
  Expense, FactuurAntwoord, Ik, IkAntwoord, Keuzes, Persoon, TekenUitkomst, Wijziging, ZoekAntwoord, ZoekVraag,
} from './types'

/* ===========================================================================
 *  Praten met de eigen server
 *
 *  Alles loopt via de Worker op hetzelfde adres; de browser heeft nergens
 *  anders een sleutel voor. Wat hij heeft is een koekje, en dat gaat vanzelf
 *  mee. Overgenomen uit Exact.
 *
 *  Elke aanroep gooit bij een mislukking, met de tekst van de server erbij,
 *  en het scherm laat die zien. Een lege lijst tonen terwijl de vraag
 *  mislukte, is hier de duurste fout: dan lijkt het of er niets op je ligt.
 * =========================================================================== */

export class Fout extends Error {
  constructor(public status: number, bericht: string, public code: string | null = null) { super(bericht) }
}

let bijUitgelogd: (() => void) | null = null
export function opUitgelogd(f: () => void) { bijUitgelogd = f }

async function lees<T>(a: Response): Promise<T> {
  let lijf: { ok?: boolean; fout?: string; code?: string | null } & Record<string, unknown> = {}
  try { lijf = await a.json() } catch { /* geen JSON: dan volgt hieronder de status */ }
  if (a.status === 401) {
    bijUitgelogd?.()
    throw new Fout(401, 'Je bent uitgelogd.')
  }
  if (!a.ok || lijf.ok === false) throw new Fout(a.status, lijf.fout ?? `De server gaf ${a.status}.`, lijf.code ?? null)
  return lijf as T
}

/** Een actie van de functie tw10mobile (zie worker/gegevens.ts voor de lijst). */
export async function x<T = Record<string, unknown>>(actie: string, lijf: object = {}): Promise<T> {
  let a: Response
  try {
    a = await fetch('/api/x', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...lijf, actie }),
    })
  } catch {
    throw new Fout(0, 'Geen verbinding. Controleer je internet en probeer het opnieuw.')
  }
  return lees<T>(a)
}

export interface Sessie { ik: Ik; verlooptOp: number }

/** Null als er niemand is ingelogd. Gooit niet: dit is de eerste vraag. */
export async function haalSessie(): Promise<Sessie | null> {
  try {
    const a = await fetch('/api/sessie', { headers: { Accept: 'application/json' } })
    if (!a.ok) return null
    const lijf = await a.json() as { ok?: boolean } & Sessie
    return lijf.ok ? lijf : null
  } catch {
    return null
  }
}

export async function uitloggen(): Promise<void> {
  try { await fetch('/api/uitloggen', { method: 'POST' }) } catch { /* vervalt vanzelf */ }
}

/** Het adres van een origineel: n = 0 is de factuur zelf. */
export const stukAdres = (id: string, n: number) => `/api/stuk/${encodeURIComponent(id)}/${n}`

/**
 * Een bon zoals de server hem stuurt, met de drie velden die het dashboard
 * altijd heeft. De server laat null weg (net als fromRow() in het
 * dashboard); werklijst.ts doet `bon.supplier.trim()` en rekent met
 * `bon.date`, en die mogen dan niet ontbreken.
 */
function heel(b: Expense): Expense {
  return { ...b, supplier: b.supplier ?? '', amountExcl: Number(b.amountExcl ?? 0), date: Number(b.date ?? 0) }
}

export const api = {
  ik: () => x<IkAntwoord>('ik'),
  werk: () => x<{ bonnen: Expense[]; op: number }>('werk').then((r) => ({ ...r, bonnen: r.bonnen.map(heel) })),
  zoek: (v: ZoekVraag, pagina: number) =>
    x<ZoekAntwoord>('zoek', { ...v, pagina }).then((r) => ({ ...r, bonnen: r.bonnen.map(heel) })),
  factuur: (id: string) => x<FactuurAntwoord>('factuur', { id }).then((r) => ({ ...r, bon: heel(r.bon) })),
  personen: () => x<{ personen: Persoon[] }>('personen').then((r) => r.personen),
  geschiedenis: () => x<{ bonnen: Expense[] }>('geschiedenis').then((r) => r.bonnen.map(heel)),

  notitie: (id: string, tekst: string) => x('notitie', { id, tekst }),
  tekenen: (v: {
    id: string; verwacht: string; aan?: string; opmerking?: string
    zelfBeideWaarom?: string; buitenRouteWaarom?: string
  }) => x<TekenUitkomst>('tekenen', v),
  afkeuren: (v: { id: string; verwacht: string; reden: string; opmerking?: string }) => x('afkeuren', v),
  terugzetten: (v: { id: string; verwacht: string; naar: string; reden: string; opmerking?: string }) => x('terugzetten', v),
  doorzetten: (v: { id: string; verwacht: string; aan: string; opmerking?: string }) => x('doorzetten', v),
  naarExact: (id: string) => x<{ gelukt: boolean; reden: string | null }>('naar-exact', { id }),
  /* Zoals de detailpagina van TW10 Online (23 okt 2026): in of uit dispuut (0240), en "toch een factuur" (0178). */
  dispuut: (id: string, aan: boolean, reden: string) => x('dispuut', { id, aan, reden }),
  tochFactuur: (id: string) => x('toch-factuur', { id }),

  /* Aanpassen (20 okt 2026). `was`: wat je zag, zodat de server het zegt als iemand anders sneller was. */
  keuzes: (id: string, bv?: string) => x<Keuzes>('keuzes', { id, bv }),
  wijzigen: (id: string, velden: Wijziging, was: Partial<Record<keyof Wijziging, unknown>>) =>
    x('wijzigen', { id, velden, was }),
  regelWijzigen: (id: string, regelId: string, deel: { grootboekCode?: string | null; kostenplaats?: string | null }) =>
    x('regel-wijzigen', { id, regelId, ...deel }),

  scanPlek: (v: { bonId: string; naam: string; mime: string; grootte: number }) =>
    x<{ pad: string; token: string }>('scan-plek', v),
  scanBon: (v: { bonId: string; naam: string; paginas: number; richting: string; bedrijf: string }) =>
    x<{ id: string; bedrijfGezet: boolean; lezen: { ok: boolean; reden: string | null }; waarschuwing: string | null }>('scan-bon', v),
}

/** De bytes van een scan naar de opslag, via de Worker (zie worker/index.ts). */
export async function uploaden(pad: string, token: string, bestand: Blob): Promise<void> {
  let a: Response
  try {
    a = await fetch(`/api/upload?pad=${encodeURIComponent(pad)}&token=${encodeURIComponent(token)}`, {
      method: 'PUT',
      headers: { 'Content-Type': bestand.type || 'application/pdf' },
      body: bestand,
    })
  } catch {
    throw new Fout(0, 'Geen verbinding tijdens het versturen. Probeer het opnieuw.')
  }
  await lees(a)
}
